import Button from '@mui/material/Button';
import {  AppBar, Card, CardActions, CardContent, CardHeader, ListItemButton } from '@mui/material';
import React, { ReactElement, useEffect } from 'react';
import Stack from '@mui/material/Stack';
import { useWorkspaceContext } from '../../../../context/workspaceContext';
import { uuid } from '../../../../utils/utils';
import EventSequenceService from '../../../../services/event-sequence.service';
import { toast } from 'react-toastify';
import { OccurrenceLink, OccurrencesLinkedToNode } from '../../../../@types/occurrence-dna';
import EventSequence from '../../../../@types/event-sequence';
import { CanvasActionType } from '../../../../@types/canvas-action-type';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Toolbar from '@mui/material/Toolbar';
import CloseIcon from '@mui/icons-material/HighlightOff';
import Avatar from '@mui/material/Avatar';
import Chip from '@mui/material/Chip';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import * as CanvasUtils from '../../../../utils/canvas/node-utils';
import { red } from '@mui/material/colors';



export default function PanelODnaList(props: { eventSequence: EventSequence, isActive: boolean, onDnaToggle: (openDnaPanel: boolean | string[]) => void }): ReactElement {
    const {   eventSequence, isActive, onDnaToggle } = props;
    
    const { currentCanvasAction, setCurrentCanvasAction, currentCanvasSelectedNode } = useWorkspaceContext();
    const [open, setOpen] = React.useState(false);
    const [selectedNodeOccurrenceList, setSelectedNodeOccurrenceList] = React.useState<OccurrencesLinkedToNode[]>([]);
    const [selectedIndexList1, setSelectedIndexList1] = React.useState(-1);
    const [selectedIndexList2, setSelectedIndexList2] = React.useState(-1);
    const uuidRef = React.useRef(uuid());

    const isEvent = (): boolean => {
        if (!currentCanvasSelectedNode) {
            return false;
        }
        return CanvasUtils.isEvent(currentCanvasSelectedNode.type);
    }

    const getDNA = (occurrenceId: number): void => {
        EventSequenceService.getLinkedOccurrenceDna(eventSequence.id, occurrenceId).then((response) => {
            if (response.events.length === 0 && response.faultTreeNodes.length === 0) {
                toast.info('No DNA found for this occurrence');
            }
            setCurrentCanvasAction({ actionType: CanvasActionType.CANVAS_PULLED_ODNA, query: response });
        }).catch((error: Error) => {
            toast.error(error.message);
        });
    }

    useEffect(() => {
        if (isActive && currentCanvasAction !== null && currentCanvasAction.actionType !== CanvasActionType.UNKNOWN) {
            switch (currentCanvasAction.actionType) {
                case CanvasActionType.CANVAS_TOGGLE_DNA_LIST:
                    setTimeout(() => onDnaToggle(!open));
                    setOpen(!open);
                    if (open) {                  
                        setCurrentCanvasAction({ actionType: CanvasActionType.CANVAS_PULLED_ODNA, query: null });
                        setSelectedNodeOccurrenceList([]);
                    }
                    break;
                case CanvasActionType.NODE_OCCURRENCE_LIST_VIEW:
                    if (currentCanvasSelectedNode) {
                        const node = currentCanvasSelectedNode;
                        const { start, end, searchTerm, psfIds } = currentCanvasAction.query;
                        const elementType = isEvent() ? 'event' : 'fault_tree_node';
                        EventSequenceService.fetchOccurrencesLinkedToNode(node.id, elementType, start, end, searchTerm, psfIds).then((response) => {
                            response.occurrenceLinks.sort((occ1, occ2) => occ1.displayName > occ2.displayName ? 1 : -1);
                            response.nodeUniqueId = node.uniqueId;
                            response.nodeName = node.name;
                            setSelectedNodeOccurrenceList(selectedNodeOccurrenceList.concat(response));
                            if (!open) {
                                setTimeout(() => onDnaToggle(true));
                                setOpen(true);
                            }
                        }).catch((error: Error) => {
                            toast.error(error.message);
                        });
                    }
                    break;
                default:
                    break;
            }
            setTimeout(() => {
                setCurrentCanvasAction({ actionType: CanvasActionType.UNKNOWN });
            }, 100);

        }

    }, [currentCanvasAction, isActive]);



    if (open === false) {
        return <></>;
    }


    return (
        <Paper id={uuidRef.current} sx={{ height: '100%', overflow: 'auto' }}>
            <Box sx={{ flexGrow: 1 }}>
                <AppBar position="static" color="primary">
                    <Toolbar>
                        <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
                            Node Occurrence List Panel
                        </Typography>
                        <Box sx={{ flexGrow: 1 }} />

                        <IconButton
                            color="inherit"
                            component='button'
                            edge='end'
                            onClick={(): void => setCurrentCanvasAction({ actionType: CanvasActionType.CANVAS_TOGGLE_DNA_LIST })}>
                            <CloseIcon sx={{ width: 25, height: 25 }} />
                        </IconButton>
                    </Toolbar>
                </AppBar>
            </Box>
            <Stack direction="column" spacing={2} justifyContent='start' sx={{ m: 1 }}>
                {selectedNodeOccurrenceList.map((occurrenceList: OccurrencesLinkedToNode, index: number) => {
                    let failures = occurrenceList.occurrenceLinks.filter((occurrence: OccurrenceLink) => occurrence.failureStatus === 'FAILURE');
                    let successes = occurrenceList.occurrenceLinks.filter((occurrence: OccurrenceLink) => occurrence.failureStatus === 'SUCCESS');
                    return (
                        <Card key={occurrenceList.safetyModelElementId + '-' + index} sx={{ width: '100%' }} variant='outlined'>
                            <CardHeader
                                avatar={<Avatar sx={{ bgcolor: red[100] }}>N</Avatar>}
                                title={occurrenceList.nodeUniqueId}
                                subheader={occurrenceList.nodeName}
                            />
                            <CardContent>
                                <Stack direction="column" spacing={2} justifyContent='start' sx={{ m: 1 }}>
                                        
                                    <Stack direction="column" spacing={2} justifyContent='start' sx={{ m: 1 }}>
                                        <Chip
                                            label={`Successes: ${successes.length}`}
                                            color='success'
                                        />
                                        <List
                                            sx={{
                                                width: '100%',
                                                bgcolor: 'background.paper',
                                                position: 'relative',
                                                overflow: 'auto',
                                                maxHeight: 200,
                                            }}
                                            dense
                                            disablePadding
                                        >
                                            {successes.map((occurrenceLink: OccurrenceLink, index: number) => (
                                                <ListItem key={occurrenceLink.id}>
                                                    <ListItemButton
                                                        selected={selectedIndexList1 === index}
                                                        onClick={(): void => { getDNA(occurrenceLink.id); setSelectedIndexList1(index); setSelectedIndexList2(-1);}}
                                                    >
                                                        <ListItemText
                                                            primary={occurrenceLink.displayName}
                                                            primaryTypographyProps={{
                                                                color: 'green',
                                                                fontWeight: 'medium',
                                                                variant: 'body2'
                                                            }}
                                                        />
                                                    </ListItemButton>
                                                </ListItem>
                                            ))}
                                        </List>
                                                             
                                    </Stack>
                                    <Stack direction="column" spacing={2} justifyContent='start' sx={{ m: 1 }}>
                                        <Chip
                                            label={`Failures: ${failures.length}`}
                                            color='error'
                                        />
                                        <List
                                            sx={{
                                                width: '100%',
                                                bgcolor: 'background.paper',
                                                position: 'relative',
                                                overflow: 'auto',
                                                maxHeight: 200,
                                            }}
                                            dense
                                            disablePadding
                                        >
                                            {failures.map((occurrenceLink: OccurrenceLink, index: number) => (
                                                <ListItem key={occurrenceLink.id}>
                                                    <ListItemButton
                                                        selected={selectedIndexList2 === index}
                                                        onClick={(): void => { getDNA(occurrenceLink.id); setSelectedIndexList2(index); setSelectedIndexList1(-1);}}
                                                    >
                                                        <ListItemText
                                                            primary={occurrenceLink.displayName}
                                                            primaryTypographyProps={{
                                                                color: 'error',
                                                                fontWeight: 'medium',
                                                                variant: 'body2'
                                                            }}
                                                        />
                                                    </ListItemButton>
                                                </ListItem>
                                            ))}
                                        </List>

                                            
                                    </Stack>
                                </Stack>
                            </CardContent>
                            <CardActions>
                                <Button
                                    size="small"
                                    color="primary"
                                    onClick={(): void => {
                                        let textInput = '';
                                        occurrenceList.occurrenceLinks.forEach((occurrenceLink: OccurrenceLink) => {
                                            textInput += occurrenceLink.displayName + ',' + occurrenceLink.failureStatus + '\n';
                                        });
                                        const element = document.createElement('a');
                                        const file = new Blob([textInput], { type: 'text/plain' });
                                        element.href = URL.createObjectURL(file);
                                        element.download = 'occurrences_' + occurrenceList.nodeUniqueId + '_download.csv';
                                        document.body.appendChild(element); // Required for this to work in FireFox
                                        element.click();
                                        document.body.removeChild(element);
                                    }}
                                >
                                    Download
                                </Button>
                            </CardActions>
                        </Card>
                    );
                })}
            </Stack>

        </Paper>
    );
}
